// src/components/Atelier3/Activite2/StrategicScenariosTable.js
import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  Search,
  Info,
  Shield,
  AlertTriangle,
  Check,
  Target,
  Save,
  Loader,
  FileText,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import useAnalysisStore from '../../../stores/useAnalysisStore';
import useAtelier1Store from '../../../stores/useAtelier1Store';
import useAtelier3Store from '../../../stores/useAtelier3Store';
import { useBroadcast } from '../../../hooks/useBroadcast';
import { showSuccessToast, showErrorToast } from "../../../utils/toastUtils";
import { createIdGenerator } from "../../../utils/idGenerator";
import workshop3Service from "../../../services/workshop3Service"; // New Hydration Service
import stakeholderService from "../../../services/stakeholderService"; // For saving stakeholders
import StrategicScenariosPdfGenerator from "../../../utils/strategicScenariosPdfGenerator";
import WorkshopExportMenu from "../../common/WorkshopExportMenu";
import Button from "../../common/Button";
import { useSafeSave } from "../../../hooks/useSafeSave";
import Workshop3Activite2CsvGenerator from "../../../utils/workshop3Activite2CsvGenerator";

// Calculate niveau based on motivation, activite, and ressources
const calculateNiveau = (motivation, activite, ressources) => {
  const normalizedMotivation = String(motivation || "").toLowerCase();
  const normalizedActivite = String(activite || "").toLowerCase();
  const normalizedRessources = String(ressources || "").toLowerCase();

  const motivationScore = normalizedMotivation.startsWith("elev")
    ? 3
    : normalizedMotivation.startsWith("moyen")
      ? 2
      : 1;
  const activiteScore = normalizedActivite.startsWith("elev")
    ? 3
    : normalizedActivite.startsWith("moyen")
      ? 2
      : 1;
  const ressourcesScore = normalizedRessources.startsWith("import")
    ? 3
    : normalizedRessources.startsWith("moyen")
      ? 2
      : 1;

  const totalScore = motivationScore + activiteScore + ressourcesScore;

  if (totalScore < 6) return 1;
  else if (totalScore === 6) return 2;
  else if (totalScore <= 7) return 3;
  else return 4;
};

// Get color for niveau (risk level)
const getNiveauColor = (niveau) => {
  switch (niveau) {
    case 1:
      return "bg-green-100 text-green-800 border border-green-300";
    case 2:
      return "bg-yellow-100 text-yellow-800 border border-yellow-300";
    case 3:
      return "bg-orange-100 text-orange-800 border border-orange-300";
    case 4:
      return "bg-red-100 text-red-800 border border-red-300";
    default:
      return "bg-gray-100 text-gray-800 border border-gray-300";
  }
};

const formatSeverity = (severity, t) => {
  if (!severity) return "-";
  // Use translation if available, otherwise capitalize
  return t
    ? t(`severity.${severity}`)
    : severity.charAt(0).toUpperCase() + severity.slice(1);
};

// Get color for severity
const getSeverityColor = (severity) => {
  switch (severity) {
    case "minor":
      return "bg-emerald-100 text-emerald-800 border border-emerald-300";
    case "moderate":
      return "bg-yellow-100 text-yellow-800 border border-yellow-300";
    case "major":
      return "bg-orange-100 text-orange-800 border border-orange-300";
    case "critical":
      return "bg-red-100 text-red-800 border border-red-300";
    case "catastrophic":
      return "bg-slate-800 text-white border border-slate-700";
    default:
      return "bg-gray-100 text-gray-800 border border-gray-300";
  }
};

const StrategicScenariosTable = forwardRef(
  (
    {
      mappings,
      sourcesRisque,
      dreadedEvents,
      controlsData,
      stakeholders,
      businessValues = [],
      initialSelectedStakeholders = {},
      onSelectedStakeholdersChange,
      onHelpClick,
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const analysisStore = useAnalysisStore();
  const atelier1Store = useAtelier1Store();
  const broadcastUpdate = useBroadcast();
  const currentAnalysis = analysisStore.currentAnalysis;
  const currentContextData = atelier1Store.contextData;

    const {
      markDataLoaded,
      markModified,
      canAutoSave,
      clearModified,
      useFormChangeDetector,
    } = useSafeSave("StrategicScenarios");
    const formChangeRef = useFormChangeDetector();

    // Merge and save stakeholders - preserves non-retained stakeholders
    const mergeAndSaveStakeholders = async (stakeholdersToSave) => {
      try {
        // Fetch ALL stakeholders from database (including non-retained)
        const fullResponse = await stakeholderService.getStakeholders(
          currentAnalysis.id,
        );
        if (
          !fullResponse ||
          !fullResponse.success ||
          !Array.isArray(fullResponse.data)
        ) {
          throw new Error("Failed to fetch full stakeholder list");
        }

        // Build a map of stakeholderId -> concernedScenarios from local changes
        const changesMap = new Map();
        stakeholdersToSave.forEach((s) => {
          changesMap.set(s.id, s.concernedScenarios || []);
        });

        // Merge: apply concernedScenarios updates to the full list
        const mergedStakeholders = fullResponse.data.map((stakeholder) => {
          if (changesMap.has(stakeholder.id)) {
            return {
              ...stakeholder,
              concernedScenarios: changesMap.get(stakeholder.id),
            };
          }
          return stakeholder;
        });

        // Save the complete merged list
        return await useAtelier3Store.getState().saveStakeholders(currentAnalysis.id, mergedStakeholders, broadcastUpdate);
      } catch (error) {
        console.error("Error merging stakeholders:", error);
        throw error;
      }
    };

    // Handle silent save without toasts
    const handleSilentSave = async () => {
      if (!canAutoSave()) return "skipped";

      if (currentAnalysis?.id) {
        try {
          console.log(
            "StrategicScenariosTable - Performing silent save of stakeholders/scenarios",
          );
          // Save with merge to preserve non-retained stakeholders
          await mergeAndSaveStakeholders(localStakeholders);
          clearModified();
          console.log("StrategicScenariosTable - Silent save successful");
          return true;
        } catch (error) {
          console.error("StrategicScenariosTable - Silent save error:", error);
          return false;
        }
      }
      return false;
    };

    // Expose silentSave to parent via ref
    useImperativeHandle(ref, () => ({
      silentSave: async () => {
        return await handleSilentSave();
      },
    }));

    // Create ID generator for this analysis
    const idGen = currentAnalysis?.id
      ? createIdGenerator({ analysisId: currentAnalysis.id })
      : null;

    const [isGuideOpen, setIsGuideOpen] = useState(false);
    const [isPdfMenuOpen, setIsPdfMenuOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [filterCategory, setFilterCategory] = useState("");
    const [selectedStakeholders, setSelectedStakeholders] = useState(
      initialSelectedStakeholders,
    );
    const [isSaving, setIsSaving] = useState(false);
    const [sortField, setSortField] = useState("scenarioNumber");
    const [sortDirection, setSortDirection] = useState("asc");

    // Hydrated data from backend
    const [hydratedScenarios, setHydratedScenarios] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [localStakeholders, setLocalStakeholders] = useState([]);

    // Initialize local stakeholders from props
    useEffect(() => {
      if (stakeholders) {
        setLocalStakeholders(stakeholders);
      }
    }, [stakeholders]);

    // Load hydrated scenarios from backend
    useEffect(() => {
      console.log(
        "🔄 StrategicScenariosTable useEffect triggered, analysisId:",
        currentAnalysis?.id,
      );
      const loadScenarios = async () => {
        if (currentAnalysis?.id) {
          console.log("📡 Loading hydrated scenarios for:", currentAnalysis.id);
          setIsLoading(true);
          try {
            // Use the new hydration service
            const response = await workshop3Service.getStrategicScenarios(
              currentAnalysis.id,
            );
            console.log("✅ Hydration response:", response);
            if (response && response.data) {
              console.log(
                "📊 Setting",
                response.data.length,
                "hydrated scenarios",
              );
              setHydratedScenarios(response.data);
            }
            markDataLoaded();
          } catch (error) {
            console.error("Error loading hydrated scenarios:", error);
            showErrorToast(t("errors.loadingError"));
          } finally {
            setIsLoading(false);
          }
        }
      };

      loadScenarios();
    }, [currentAnalysis?.id, mappings]); // Reload if mappings change

    // Handle sort click
    const handleSort = (field) => {
      if (sortField === field) {
        setSortDirection(sortDirection === "asc" ? "desc" : "asc");
      } else {
        setSortField(field);
        setSortDirection("asc");
      }
    };

    // Severity weight mapping for sorting
    const severityWeight = {
      catastrophic: 5,
      critical: 4,
      major: 3,
      moderate: 2,
      minor: 1,
      undefined: 0,
    };

    // Get all unique categories
    const categories = [
      ...new Set(sourcesRisque.map((source) => source.category)),
    ];

    // Update local state when initial selected stakeholders change
    useEffect(() => {
      setSelectedStakeholders(initialSelectedStakeholders);
    }, [initialSelectedStakeholders]);

    // Create a combined data structure with all the information
    // Create a combined data structure with all the information
    // Now using hydrated scenarios from the backend
    const tableData = useMemo(() => {
      if (!hydratedScenarios || hydratedScenarios.length === 0) return [];

      return hydratedScenarios.map((scenario) => {
        return {
          id: scenario.id,
          sourceId: scenario.sourceRiskId,
          dreadedEventId: scenario.dreadedEventId,
          // Map to properties expected by the table rendering
          sourceRisk: scenario.sourceRiskName,
          sourceCategory: scenario.sourceCategory,
          objectifVise: scenario.objectifVise,
          objectifViseCategory: scenario.objectifViseCategory,
          dreadedEvent: scenario.dreadedEventName,
          dreadedEventDescription: scenario.dreadedEventDescription || "",
          severity: scenario.severity,
          controls: scenario.controls || [],
          businessValue: scenario.businessValueName,
          businessValueId: scenario.businessValueId,
          supportAssets: scenario.supportAssets || [],
          niveau: scenario.niveau,
          scenarioNumber: scenario.scenarioNumber,

          // Keep original objects if needed
          fullSourceRisk: scenario.fullSourceRisk,
          fullDreadedEvent: scenario.fullDreadedEvent,

          // Other props used
          sourceDescription: scenario.sourceDescription,
        };
      });
    }, [hydratedScenarios]);

    // Filter data based on search term and category filter
    const filteredData = useMemo(() => {
      return tableData.filter((item) => {
        const matchesSearch =
          item.sourceRisk.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.objectifVise.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.dreadedEvent.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesCategory = filterCategory
          ? item.sourceCategory === filterCategory
          : true;

        return matchesSearch && matchesCategory;
      });
    }, [tableData, searchTerm, filterCategory]);

    // Create a mapping for unique scenario numbers for each row
    // We use the full tableData so that scenario numbers remain stable even when filtering
    const scenarioNumberMapping = useMemo(() => {
      const mapping = {};
      let scenarioCounter = 1;

      // Sort all available scenarios alphabetically to assign fixed numbers
      const stableSortedAll = [...tableData].sort((a, b) => {
        const deComp = a.dreadedEvent.localeCompare(b.dreadedEvent);
        if (deComp !== 0) return deComp;
        return a.sourceRisk.localeCompare(b.sourceRisk);
      });

      // Assign a unique scenario number to each unique source-dreaded event combination
      stableSortedAll.forEach((item) => {
        const key = `${item.sourceId}-${item.dreadedEventId}`;
        if (!mapping[key]) {
          mapping[key] = scenarioCounter++;
        }
      });

      return mapping;
    }, [tableData]);

    // Sort data dynamically
    const sortedData = useMemo(() => {
      return [...filteredData].sort((a, b) => {
        let comparison = 0;

        switch (sortField) {
          case "scenarioNumber": {
            const numA =
              scenarioNumberMapping[`${a.sourceId}-${a.dreadedEventId}`] || 0;
            const numB =
              scenarioNumberMapping[`${b.sourceId}-${b.dreadedEventId}`] || 0;
            comparison = numA - numB;
            break;
          }
          case "srOv": {
            const textA = `${a.sourceRisk} ${a.objectifVise}`.toLowerCase();
            const textB = `${b.sourceRisk} ${b.objectifVise}`.toLowerCase();
            comparison = textA.localeCompare(textB);
            break;
          }
          case "dreadedEvent": {
            comparison = a.dreadedEvent.localeCompare(b.dreadedEvent);
            break;
          }
          case "severity": {
            const weightA = severityWeight[a.severity] || 0;
            const weightB = severityWeight[b.severity] || 0;
            comparison = weightA - weightB;
            break;
          }
          case "stakeholderThreat": {
            const getRowMaxThreat = (item) => {
              const selectedIds =
                selectedStakeholders[
                  `${item.sourceId}-${item.dreadedEventId}`
                ] || [];
              if (selectedIds.length === 0) return 0;
              const threats = stakeholders
                .filter((s) => selectedIds.includes(s.id))
                .map((s) => s.threatLevel || 0);
              return threats.length > 0 ? Math.max(...threats) : 0;
            };
            comparison = getRowMaxThreat(a) - getRowMaxThreat(b);
            break;
          }
          default:
            comparison = 0;
        }

        // If comparison is 0, fallback to scenario number for stability
        if (comparison === 0 && sortField !== "scenarioNumber") {
          const numA =
            scenarioNumberMapping[`${a.sourceId}-${a.dreadedEventId}`] || 0;
          const numB =
            scenarioNumberMapping[`${b.sourceId}-${b.dreadedEventId}`] || 0;
          comparison = numA - numB;
        }

        return sortDirection === "asc" ? comparison : -comparison;
      });
    }, [
      filteredData,
      sortField,
      sortDirection,
      scenarioNumberMapping,
      selectedStakeholders,
      stakeholders,
    ]);

    // Process data for merged cells
    const processedData = useMemo(() => {
      if (sortedData.length === 0) return [];

      // Create a new array with rowspan information
      const result = [];

      // Track current dreaded event, source and objectif for rowspan calculation
      let currentDreadedEventId = null;
      let currentDreadedEventIndex = -1;
      let currentSourceId = null;
      let currentSourceIndex = -1;
      let currentObjectifValue = null;
      let currentObjectifIndex = -1;

      sortedData.forEach((item, index) => {
        // Create a new item with rowspan information
        const newItem = {
          ...item,
          dreadedEventRowspan: 1,
          sourceRowspan: 1,
          objectifRowspan: 1,
          // Calculate scenario number based on the unique source-dreaded event combination
          scenarioNumber:
            scenarioNumberMapping[`${item.sourceId}-${item.dreadedEventId}`],
          // Ensure all cells have values, even if empty
          sourceRisk: item.sourceRisk || "",
          sourceCategory: item.sourceCategory || "",
          objectifVise: item.objectifVise || "",
          objectifViseCategory: item.objectifViseCategory || "",
          dreadedEvent: item.dreadedEvent || "",
          controls: item.controls || [],
        };

        // Check if this is the same dreaded event as the previous one
        if (item.dreadedEventId === currentDreadedEventId) {
          // Same dreaded event, increment the rowspan of the first occurrence
          result[currentDreadedEventIndex].dreadedEventRowspan++;
          // Mark this item to not display the scenario number
          newItem.hideDreadedEvent = true;
        } else {
          // New dreaded event
          currentDreadedEventId = item.dreadedEventId;
          currentDreadedEventIndex = result.length;
        }

        // Check if this is a new source or the same as the previous one
        if (item.sourceId === currentSourceId) {
          // Same source, increment the rowspan of the first occurrence
          result[currentSourceIndex].sourceRowspan++;
          // Mark this item to not display the source
          newItem.hideSource = true;

          // Check if objectif is the same as the current objectif
          if (item.objectifVise === currentObjectifValue) {
            // Same objectif, increment the rowspan of the first occurrence
            result[currentObjectifIndex].objectifRowspan++;
            // Mark this item to not display the objectif
            newItem.hideObjectif = true;
          } else {
            // New objectif within the same source
            currentObjectifValue = item.objectifVise;
            currentObjectifIndex = result.length;
          }
        } else {
          // New source
          currentSourceId = item.sourceId;
          currentSourceIndex = result.length;

          // Reset objectif tracking for the new source
          currentObjectifValue = item.objectifVise;
          currentObjectifIndex = result.length;
        }

        result.push(newItem);
      });

      return result;
    }, [sortedData, scenarioNumberMapping]);

    // Format control decision status
    const formatDecisionStatus = (decision) => {
      switch (decision) {
        case "implemented":
          return (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-800 border border-green-200">
              <Check size={12} className="mr-1" />
              {t("workshop3.activity2.controlStatus.implemented")}
            </span>
          );
        case "planned":
          return (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-800 border border-blue-200">
              <Shield size={12} className="mr-1" />
              {t("workshop3.activity2.controlStatus.planned")}
            </span>
          );
        default:
          return (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-50 text-gray-800 border border-gray-200">
              <Info size={12} className="mr-1" />
              {t("workshop3.activity2.controlStatus.notDefined")}
            </span>
          );
      }
    };

    // Handle stakeholder selection - updates the localStakeholders state
    const handleStakeholderSelection = useCallback(
      (sourceId, dreadedEventId, stakeholderId, isChecked) => {
        // Determine the scenario key as sourceId-dreadedEventId
        const scenarioKey = `${sourceId}-${dreadedEventId}`;

        setLocalStakeholders((prevStakeholders) => {
          return prevStakeholders.map((stakeholder) => {
            // Find the stakeholder we need to update
            if (stakeholder.id !== stakeholderId) return stakeholder;

            // Clone to avoid mutation
            const newStakeholder = { ...stakeholder };
            // Ensure concernedScenarios array exists
            const concernedScenarios = Array.isArray(
              newStakeholder.concernedScenarios,
            )
              ? [...newStakeholder.concernedScenarios]
              : [];

            if (isChecked) {
              // Add the scenario key if it's not already there
              if (!concernedScenarios.includes(scenarioKey)) {
                concernedScenarios.push(scenarioKey);
              }
            } else {
              // Remove the scenario key if it exists
              const index = concernedScenarios.indexOf(scenarioKey);
              if (index > -1) {
                concernedScenarios.splice(index, 1);
              }
            }

            newStakeholder.concernedScenarios = concernedScenarios;
            return newStakeholder;
          });
        });

        markModified();
      },
      [markModified],
    );

    // Prepare attack paths data for saving (original functionality for backward compatibility)
    const prepareAttackPathsData = useCallback(() => {
      const attackPaths = [];

      // Loop through all processed data items
      processedData.forEach((item) => {
        // Get the key for this source-dreaded event pair
        const key = `${item.sourceId}-${item.dreadedEventId}`;

        // Get the selected stakeholder IDs for this item
        const selectedStakeholderIds = selectedStakeholders[key] || [];

        // Get the stakeholder objects for the selected IDs
        const selectedStakeholdersForItem = stakeholders
          .filter((s) => s.retained && selectedStakeholderIds.includes(s.id))
          .map((s) => ({ id: s.id, name: s.name }));

        // Create a separate attack path for each selected stakeholder
        selectedStakeholdersForItem.forEach((stakeholder) => {
          attackPaths.push({
            id: `${item.sourceId}-${item.dreadedEventId}-${stakeholder.id}`,
            sourceRiskId: item.sourceId,
            sourceRiskName: item.sourceRisk,
            objectifVise: item.objectifVise,
            dreadedEventId: item.dreadedEventId,
            dreadedEventName: item.dreadedEvent,
            businessValueId: item.businessValueId,
            businessValueName: item.businessValue,
            stakeholders: [stakeholder], // Only one stakeholder per attack path
            severity: item.severity, // Add severity
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          });
        });
      });

      return attackPaths;
    }, [processedData, selectedStakeholders, stakeholders]);

    // Prepare strategic scenarios data for saving
    const prepareStrategicScenariosData = useCallback(() => {
      const strategicScenarios = [];
      const allAttackPaths = []; // Collect all attack paths for sequential ID generation

      // Loop through all processed data items to create strategic scenarios
      processedData.forEach((item, scenarioIndex) => {
        // Get the key for this source-dreaded event pair
        const key = `${item.sourceId}-${item.dreadedEventId}`;

        // Get the selected stakeholder IDs for this item
        const selectedStakeholderIds = selectedStakeholders[key] || [];

        // Get the stakeholder objects for the selected IDs
        const selectedStakeholdersForItem = stakeholders
          .filter((s) => s.retained && selectedStakeholderIds.includes(s.id))
          .map((s) => ({ id: s.id, name: s.name }));

        // Create attack paths for this strategic scenario
        const attackPaths = [];

        // Always add the direct attack path
        const directAttackPath = {
          id: idGen
            ? idGen.generateNext("attackPath", [
                ...strategicScenarios.flatMap((s) => s.attackPaths || []),
                ...allAttackPaths,
              ])
            : `${item.sourceId}-${item.dreadedEventId}-direct`,
          type: "direct",
          name: `Attaque directe - ${item.sourceRisk}`,
          referenceCode: `CAD${String(item.scenarioNumber).padStart(2, "0")}`,
          stakeholders: [], // Direct attack has no stakeholders
          severity: item.severity,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        attackPaths.push(directAttackPath);
        allAttackPaths.push(directAttackPath);

        // Add attack paths for each selected stakeholder (if any)
        selectedStakeholdersForItem.forEach((stakeholder, index) => {
          const stakeholderAttackPath = {
            id: idGen
              ? idGen.generateNext("attackPath", [
                  ...strategicScenarios.flatMap((s) => s.attackPaths || []),
                  ...allAttackPaths,
                ])
              : `${item.sourceId}-${item.dreadedEventId}-${stakeholder.id}`,
            type: "stakeholder",
            name: `Attaque via ${stakeholder.name}`,
            referenceCode: `CA${String(allAttackPaths.filter((ap) => ap.type === "stakeholder").length + 1).padStart(2, "0")}`,
            stakeholders: [stakeholder],
            severity: item.severity,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          };
          attackPaths.push(stakeholderAttackPath);
          allAttackPaths.push(stakeholderAttackPath);
        });

        // Create the strategic scenario with significant ID
        const strategicScenario = {
          id: idGen
            ? idGen.generateNext("strategicScenario", strategicScenarios)
            : `strategic-scenario-${item.sourceId}-${item.dreadedEventId}`,
          scenarioNumber: item.scenarioNumber,
          referenceCode: `S${String(item.scenarioNumber).padStart(2, "0")}`,
          name: `Scénario ${item.scenarioNumber}: ${item.sourceRisk} → ${item.dreadedEvent}`,

          // Core scenario data
          sourceRiskId: item.sourceId,
          sourceRiskName: item.sourceRisk,
          sourceCategory: item.sourceCategory,
          objectifVise: item.objectifVise,
          objectifViseCategory: item.objectifViseCategory,

          dreadedEventId: item.dreadedEventId,
          dreadedEventName: item.dreadedEvent,
          dreadedEventDescription: item.dreadedEventDescription,
          severity: item.severity,

          businessValueId: item.businessValueId,
          businessValueName: item.businessValue,
          supportAssets: item.supportAssets || [],

          // Controls and measures
          controls: item.controls || [],

          // Selected stakeholders for this scenario
          selectedStakeholders: selectedStakeholdersForItem,

          // Attack paths generated for this strategic scenario
          attackPaths: attackPaths,

          // Metadata
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),

          // Additional context
          fullSourceRisk: item.fullSourceRisk,
          fullDreadedEvent: item.fullDreadedEvent,
        };

        strategicScenarios.push(strategicScenario);
      });

      return strategicScenarios;
    }, [processedData, selectedStakeholders, stakeholders, idGen]);

    // Handle saving strategic scenarios
    // Handle saving strategic scenarios
    // In the new architecture, this only needs to save the stakeholders
    // because the scenarios are hydrated on-the-fly based on mappings and stakeholders
    const handleSaveStrategicScenarios = useCallback(async () => {
      if (!currentAnalysis || !currentAnalysis.id) {
        showErrorToast(t("workshop2.activity1.errors.noAnalysisSelected"));
        return;
      }

      setIsSaving(true);

      try {
        console.log(
          "Saving updated stakeholders with scenario selections:",
          localStakeholders,
        );

        // Save with merge to preserve non-retained stakeholders
        await mergeAndSaveStakeholders(localStakeholders);

        console.log("Stakeholders saved successfully");

        // Refresh hydrated data to ensure everything is in sync
        const response = await workshop3Service.getStrategicScenarios(
          currentAnalysis.id,
        );
        if (response && response.data) {
          setHydratedScenarios(response.data);
        }

        clearModified();

        // Show success message
        showSuccessToast(
          t("workshop3.activity2.saveSuccess") ||
            "Scenarios updated successfully",
        );
      } catch (error) {
        console.error(
          "Error saving strategic scenarios (stakeholders):",
          error,
        );
        showErrorToast(t("errors.saveError") || "Error saving changes");
      } finally {
        setIsSaving(false);
      }
    }, [currentAnalysis, localStakeholders, t]);

    // Handle PDF Export
    const handleExportPDF = async () => {
      try {
        const generator = new StrategicScenariosPdfGenerator(
          {
            mappings,
            sourcesRisque,
            dreadedEvents,
            controlsData,
            stakeholders,
            businessValues,
            selectedStakeholders,
            analysisName: currentAnalysis?.name,
            organizationName:
              currentContextData?.organizationName || "Organization",
            analysisId: currentAnalysis?.id,
          },
          currentAnalysis,
        );

        const blob = await generator.generateReport();
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `scenarios-strategiques-${currentAnalysis?.name || "export"}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        showSuccessToast("Rapport PDF généré avec succès");
      } catch (error) {
        console.error("Error generating PDF:", error);
        showErrorToast("Erreur lors de la génération du PDF");
      }
    };

    // Handle English PDF Export
    const handleExportPDFEn = async () => {
      try {
        // Import bilingual PDF generator (EN mode)
        const { default: StrategicScenariosPdfGenerator } =
          await import("../../../utils/strategicScenariosPdfGenerator.js");

        const generator = new StrategicScenariosPdfGenerator(
          {
            mappings,
            sourcesRisque,
            dreadedEvents,
            controlsData,
            stakeholders,
            businessValues,
            selectedStakeholders,
            analysisName: currentAnalysis?.name,
            organizationName:
              currentContextData?.organizationName || "Organization",
            analysisId: currentAnalysis?.id,
          },
          currentAnalysis,
          "en"
        );

        const blob = await generator.generateReport();
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `strategic-scenarios-${currentAnalysis?.name || "export"}-EN.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        showSuccessToast("PDF Report (EN) generated successfully");
      } catch (error) {
        console.error("Error generating PDF (EN):", error);
        showErrorToast("Error generating PDF (EN)");
      }
    };

    // Handle DOCX Export
    const handleExportDocx = async () => {
      try {
        // Import DOCX generator dynamically
        const { generateStrategicScenariosDocx } =
          await import("../../../utils/strategicScenariosDocxGenerator.js");

        await generateStrategicScenariosDocx(
          {
            mappings,
            sourcesRisque,
            dreadedEvents,
            controlsData,
            stakeholders,
            businessValues,
            selectedStakeholders,
          },
          currentAnalysis,
        );

        showSuccessToast("Rapport Word généré avec succès");
      } catch (error) {
        console.error("Error generating DOCX:", error);
        showErrorToast("Erreur lors de la génération du document Word");
      }
    };

    // Handle English DOCX Export
    const handleExportDocxEn = async () => {
      try {
        // Import DOCX generator dynamically
        const { generateStrategicScenariosDocx } =
          await import("../../../utils/strategicScenariosDocxGenerator.js");

        await generateStrategicScenariosDocx(
          {
            mappings,
            sourcesRisque,
            dreadedEvents,
            controlsData,
            stakeholders,
            businessValues,
            selectedStakeholders,
          },
          currentAnalysis,
          "en"
        );

        showSuccessToast("Word Report (EN) generated successfully");
      } catch (error) {
        console.error("Error generating DOCX (EN):", error);
        showErrorToast("Error generating Word (EN) document");
      }
    };

    // Handle French CSV export
    const handleExportCsvFr = () => {
      const scenariosData = hydratedScenarios.map((s) => ({
        scenarioNumber: s.scenarioNumber,
        sourceRisk: s.sourceRiskName,
        sourceCategory: s.sourceCategory,
        target: s.objectifVise,
        dreadedEvent: s.dreadedEventName,
        severity: s.severity,
        niveau: s.niveau,
        businessValue: s.businessValueName,
        objective: s.objectifVise,
        controls: s.controls || [],
      }));
      const generator = new Workshop3Activite2CsvGenerator(
        {
          scenarios: scenariosData,
          organizationName: currentAnalysis?.organizationName || "Organisation",
        },
        "fr",
      );
      generator.download(
        `EBIOS-RM_Workshop3_Activite2_${currentAnalysis?.organizationName || "Organisation"}_${new Date().toISOString().split("T")[0]}.xls`,
      );
      showSuccessToast("Export CSV (FR) généré avec succès");
    };

    // Handle English CSV export
    const handleExportCsvEn = () => {
      const scenariosData = hydratedScenarios.map((s) => ({
        scenarioNumber: s.scenarioNumber,
        sourceRisk: s.sourceRiskName,
        sourceCategory: s.sourceCategory,
        target: s.objectifVise,
        dreadedEvent: s.dreadedEventName,
        severity: s.severity,
        niveau: s.niveau,
        businessValue: s.businessValueName,
        objective: s.objectifVise,
        controls: s.controls || [],
      }));
      const generator = new Workshop3Activite2CsvGenerator(
        {
          scenarios: scenariosData,
          organizationName: currentAnalysis?.organizationName || "Organisation",
        },
        "en",
      );
      generator.download(
        `EBIOS-RM_Workshop3_Activity2_${currentAnalysis?.organizationName || "Organisation"}_${new Date().toISOString().split("T")[0]}.xls`,
      );
      showSuccessToast("CSV Export (EN) generated successfully");
    };

    return (
      <div ref={formChangeRef} className="space-y-6">
        {/* Enhanced Header - Matching Context.js style */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-4 mb-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            {/* Left Side: Breadcrumb & Title */}
            <div>
              <div className="flex items-center text-sm text-slate-500 mb-2">
                <span className="hover:text-blue-600 transition-colors">
                  {t("workshop3.title")}
                </span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4 mx-2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
                <span className="text-blue-600 font-medium">
                  {t("workshop3.activity2.breadcrumb")}
                </span>
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 flex items-center">
                <Shield size={28} className="mr-3 text-blue-600" />
                {t("workshop3.activity2.title")}
              </h1>
            </div>

            {/* Right Side: Action Buttons */}
            <div className="flex items-center space-x-3">
              {/* Unified Export Menu */}
              <WorkshopExportMenu
                onExportPdfFr={handleExportPDF}
                onExportPdfEn={handleExportPDFEn}
                onExportDocxFr={handleExportDocx}
                onExportDocxEn={handleExportDocxEn}
                onExportCsvFr={handleExportCsvFr}
                onExportCsvEn={handleExportCsvEn}
              />

              {/* Save Button */}
              <Button
                variant="primary"
                color="blue"
                icon={<Save />}
                onClick={handleSaveStrategicScenarios}
                loading={isSaving}
                title={t("common.save")}
              >
                {t("common.save")}
              </Button>

              {/* Help Button */}
              <Button
                variant="secondary"
                color="white"
                icon={<Info />}
                onClick={onHelpClick}
                title={t("common.help")}
              >
                {t("common.help")}
              </Button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-xl shadow-lg border border-slate-100 overflow-hidden">
          {/* Table Header with Search and Filters */}
          <div className="p-5 border-b border-slate-200 bg-gradient-to-r from-blue-50 to-white">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h2 className="text-xl font-semibold text-slate-800 flex items-center">
                  <Target size={20} className="mr-2 text-blue-600" />
                  {t("workshop3.activity2.title")}
                </h2>
                <p className="text-sm text-slate-500 mt-1">
                  {t("workshop3.activity2.subtitle")}
                </p>
              </div>
              <div className="flex items-center space-x-3">
                {/* Search input */}
                <div className="relative">
                  <input
                    type="text"
                    placeholder={t("workshop3.activity2.searchPlaceholder")}
                    className="pl-9 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 w-full md:w-64 shadow-sm"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <Search
                    size={16}
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400"
                  />
                </div>

                {/* Category filter */}
                <select
                  className="border border-slate-300 rounded-lg px-3 py-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm bg-white"
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value)}
                >
                  <option value="">
                    {t("workshop3.activity2.allCategories")}
                  </option>
                  {categories.map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Table with horizontal scrolling */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200 border-collapse">
              <thead className="bg-gradient-to-r from-slate-50 to-slate-100 sticky top-0 z-10">
                <tr className="border-b-2 border-slate-300">
                  <th
                    scope="col"
                    className="px-2 py-2 text-center text-[10px] font-bold text-slate-800 uppercase tracking-wider bg-gradient-to-r from-blue-50 to-blue-100 border-r border-slate-300 w-[5%] shadow-sm cursor-pointer hover:bg-blue-200 transition-colors"
                    onClick={() => handleSort("scenarioNumber")}
                  >
                    <div className="flex items-center justify-center">
                      <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-blue-600 text-white font-bold text-[9px] mr-1">
                        S
                      </span>
                      <span className="hidden sm:inline">
                        {t("workshop3.activity2.table.scenarios")}
                      </span>
                      {sortField === "scenarioNumber" &&
                        (sortDirection === "asc" ? (
                          <ChevronUp size={12} className="ml-1" />
                        ) : (
                          <ChevronDown size={12} className="ml-1" />
                        ))}
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="px-2 py-2 text-center text-[10px] font-semibold text-slate-700 uppercase tracking-wider border-r border-slate-300 w-[22%] cursor-pointer hover:bg-slate-100 transition-colors"
                    onClick={() => handleSort("srOv")}
                  >
                    <div className="flex items-center justify-center">
                      {t("workshop3.activity2.table.riskSource")} /{" "}
                      {t("workshop3.activity2.table.targetObjective")}
                      {sortField === "srOv" &&
                        (sortDirection === "asc" ? (
                          <ChevronUp size={12} className="ml-1" />
                        ) : (
                          <ChevronDown size={12} className="ml-1" />
                        ))}
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="px-2 py-2 text-left text-[10px] font-semibold text-slate-700 uppercase tracking-wider border-r border-slate-300 w-[18%] cursor-pointer hover:bg-slate-100 transition-colors"
                    onClick={() => handleSort("dreadedEvent")}
                  >
                    <div className="flex items-center">
                      {t("workshop3.activity2.table.dreadedEvent")}
                      {sortField === "dreadedEvent" &&
                        (sortDirection === "asc" ? (
                          <ChevronUp size={12} className="ml-1" />
                        ) : (
                          <ChevronDown size={12} className="ml-1" />
                        ))}
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="px-2 py-2 text-left text-[10px] font-semibold text-slate-700 uppercase tracking-wider border-r border-slate-300 w-[18%] cursor-pointer hover:bg-slate-100 transition-colors"
                    onClick={() => handleSort("severity")}
                  >
                    <div className="flex items-center">
                      {t("workshop3.activity2.table.businessValueAssets")}
                      {sortField === "severity" &&
                        (sortDirection === "asc" ? (
                          <ChevronUp size={12} className="ml-1" />
                        ) : (
                          <ChevronDown size={12} className="ml-1" />
                        ))}
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="px-2 py-2 text-left text-[10px] font-semibold text-slate-700 uppercase tracking-wider border-r border-slate-300 w-[18%]"
                  >
                    <div className="flex items-center">
                      {t("workshop3.activity2.table.controlMeasures")}
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="px-2 py-2 text-left text-[10px] font-semibold text-slate-700 uppercase tracking-wider w-[19%] cursor-pointer hover:bg-slate-100 transition-colors"
                    onClick={() => handleSort("stakeholderThreat")}
                  >
                    <div className="flex items-center">
                      {t("workshop3.activity2.table.concernedStakeholders")}
                      {sortField === "stakeholderThreat" &&
                        (sortDirection === "asc" ? (
                          <ChevronUp size={12} className="ml-1" />
                        ) : (
                          <ChevronDown size={12} className="ml-1" />
                        ))}
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {filteredData.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-4 py-6 text-center">
                      <div className="w-12 h-12 mx-auto bg-slate-100 rounded-full flex items-center justify-center mb-3">
                        <Search size={20} className="text-slate-400" />
                      </div>
                      <h3 className="text-sm font-medium text-slate-800 mb-1">
                        {t("workshop3.activity2.empty.noScenariosFound")}
                      </h3>
                      <p className="text-xs text-slate-500">
                        {t("workshop3.activity2.empty.modifySearchCriteria")}
                      </p>
                    </td>
                  </tr>
                ) : (
                  processedData.map((item, index) => (
                    <tr
                      key={`${item.sourceId}-${item.dreadedEventId}`}
                      className="hover:bg-blue-50/30 even:bg-slate-50/50 odd:bg-white border-b border-slate-200 transition-colors duration-150"
                    >
                      {/* Scénario Stratégique - Compact */}
                      <td className="px-2 py-2 text-xs font-medium text-slate-900 border-r border-slate-200 text-center bg-gradient-to-r from-blue-50/60 to-blue-100/40">
                        <span className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-gradient-to-r from-blue-600 to-blue-700 text-white font-bold text-[10px] shadow-sm">
                          S{String(item.scenarioNumber).padStart(2, "0")}
                        </span>
                      </td>

                      {/* SR / OV with Risk Level - no rowspan, centered & middle */}
                      <td className="px-2 py-2 text-xs text-slate-900 border-r border-slate-200 align-middle text-left">
                        {/* Source de Risque */}
                        <div className="mb-1.5">
                          <div className="text-[9px] text-blue-600 font-medium uppercase tracking-wide">
                            {t("workshop3.activity2.table.riskSource")}
                          </div>
                          <div className="font-semibold text-blue-800 text-[11px] leading-tight">
                            {item.sourceRisk}
                          </div>
                          <span className="inline-block mt-0.5 px-1 py-0.5 text-[8px] font-medium rounded bg-blue-50 text-blue-600 border border-blue-100">
                            {item.sourceCategory}
                          </span>
                        </div>

                        {/* Objectif Visé */}
                        <div className="mb-1.5 pt-1 border-t border-slate-100">
                          <div className="text-[9px] text-indigo-600 font-medium uppercase tracking-wide">
                            {t("workshop3.activity2.table.targetObjective")}
                          </div>
                          <div className="font-semibold text-indigo-800 text-[11px] leading-tight">
                            {item.objectifVise || "-"}
                          </div>
                          {item.objectifViseCategory && (
                            <span className="inline-block mt-0.5 px-1 py-0.5 text-[8px] font-medium rounded bg-indigo-50 text-indigo-600 border border-indigo-100">
                              {item.objectifViseCategory}
                            </span>
                          )}
                        </div>

                        {/* Risk Level Badge */}
                        <div className="pt-2 border-t border-slate-100 flex items-center">
                          <span className="text-[10px] text-slate-500 mr-1.5 font-medium">
                            {t("workshop3.activity2.table.level", "Niv")}:
                          </span>
                          <span
                            className={`inline-flex items-center justify-center px-2 py-0.5 text-xs font-extrabold rounded-md shadow-sm ${getNiveauColor(item.niveau)}`}
                          >
                            {item.niveau}
                          </span>
                        </div>
                      </td>

                      {/* Événement Redouté with Severity */}
                      <td className="px-2 py-2 text-xs text-slate-900 border-r border-slate-200">
                        <div className="font-semibold text-amber-800 text-xs leading-tight mb-1">
                          {item.dreadedEvent}
                        </div>
                        {item.dreadedEventDescription && (
                          <div className="text-[10px] text-slate-500 leading-snug mb-1.5">
                            {item.dreadedEventDescription.length > 50
                              ? `${item.dreadedEventDescription.substring(0, 50)}...`
                              : item.dreadedEventDescription}
                          </div>
                        )}
                        {/* Severity Badge */}
                        <div className="mt-1">
                          <span
                            className={`inline-block px-2 py-0.5 text-[10px] font-bold rounded shadow-sm ${getSeverityColor(item.severity)}`}
                          >
                            {t(`severity.${item.severity}`)}
                          </span>
                        </div>
                      </td>

                      {/* Valeur Métier & Biens Support - Compact - Show all */}
                      <td className="px-2 py-2 text-xs text-slate-900 border-r border-slate-200">
                        <div className="font-semibold text-green-800 text-xs mb-1">
                          {item.businessValue}
                        </div>
                        {item.supportAssets && item.supportAssets.length > 0 ? (
                          <div className="space-y-1">
                            <div className="text-[9px] font-medium text-slate-500">
                              {t("workshop3.activity2.table.supportAssets")}:
                            </div>
                            {item.supportAssets.map((asset) => (
                              <div
                                key={asset.id}
                                className="flex items-center bg-green-50 px-1.5 py-0.5 rounded text-[10px] border border-green-100"
                              >
                                <span className="font-medium text-green-700 mr-1">
                                  {asset.shortId}
                                </span>
                                <span className="text-slate-600 truncate">
                                  {asset.name}
                                </span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-[10px] text-gray-400 italic">
                            -
                          </div>
                        )}
                      </td>

                      {/* Mesures de Contrôle - Compact - Show all */}
                      <td className="px-2 py-2 text-xs text-slate-900 border-r border-slate-200">
                        {item.controls && item.controls.length > 0 ? (
                          <div className="space-y-1">
                            {item.controls.map((control, index) => (
                              <div
                                key={index}
                                className="bg-purple-50 px-1.5 py-1 rounded border border-purple-100"
                              >
                                <div className="font-medium text-purple-800 text-[10px] leading-tight">
                                  {control.name || control.controlName}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <span className="text-[10px] text-slate-400 italic">
                            -
                          </span>
                        )}
                      </td>

                      {/* Parties Prenantes Concernées - Compact */}
                      <td className="px-2 py-2 text-xs text-slate-900">
                        {localStakeholders && localStakeholders.length > 0 ? (
                          <div className="space-y-1">
                            {localStakeholders
                              .filter((s) => s.retained)
                              .map((stakeholder) => {
                                const scenarioKey = `${item.sourceId}-${item.dreadedEventId}`;
                                const isChecked =
                                  Array.isArray(
                                    stakeholder.concernedScenarios,
                                  ) &&
                                  stakeholder.concernedScenarios.includes(
                                    scenarioKey,
                                  );

                                return (
                                  <div
                                    key={stakeholder.id}
                                    className="flex items-center bg-teal-50 px-1.5 py-0.5 rounded border border-teal-100"
                                  >
                                    <input
                                      type="checkbox"
                                      id={`stakeholder-${stakeholder.id}-${item.sourceId}-${item.dreadedEventId}`}
                                      className="h-3 w-3 text-teal-600 rounded border-slate-300 focus:ring-teal-500 focus:ring-1"
                                      checked={isChecked}
                                      onChange={(e) =>
                                        handleStakeholderSelection(
                                          item.sourceId,
                                          item.dreadedEventId,
                                          stakeholder.id,
                                          e.target.checked,
                                        )
                                      }
                                    />
                                    <label
                                      htmlFor={`stakeholder-${stakeholder.id}-${item.sourceId}-${item.dreadedEventId}`}
                                      className="ml-1.5 text-[10px] text-teal-800 font-medium truncate cursor-pointer"
                                      title={stakeholder.name}
                                    >
                                      {stakeholder.name}
                                      {stakeholder.threatLevel && (
                                        <span className="ml-1 text-[10px] opacity-90 font-bold text-slate-600">
                                          ({stakeholder.threatLevel.toFixed(1)})
                                        </span>
                                      )}
                                    </label>
                                  </div>
                                );
                              })}
                          </div>
                        ) : (
                          <span className="text-[10px] text-slate-400 italic">
                            -
                          </span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Guide Modal */}
        {isGuideOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-slate-800 flex items-center">
                  <Info size={20} className="mr-2 text-blue-600" />
                  {t("workshop3.activity2.guide.title")}
                </h2>
                <button
                  onClick={() => setIsGuideOpen(false)}
                  className="text-slate-500 hover:text-slate-700 focus:outline-none"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <div className="space-y-4 text-slate-700">
                <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 mb-4">
                  <h3 className="font-semibold text-slate-800 mb-2">
                    {t("workshop3.activity2.guide.context.title")}
                  </h3>
                  <p className="text-slate-600">
                    {t("workshop3.activity2.guide.context.description")}
                  </p>
                </div>

                <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
                  <h3 className="font-semibold text-blue-800 mb-2">
                    {t("workshop3.activity2.guide.understanding.title")}
                  </h3>
                  <ul className="list-disc pl-5 space-y-2">
                    <li>
                      <strong>
                        {t("workshop3.activity2.guide.understanding.scenarios")}
                      </strong>{" "}
                      -{" "}
                      {t(
                        "workshop3.activity2.guide.understanding.scenariosDesc",
                      )}
                    </li>
                    <li>
                      <strong>
                        {t(
                          "workshop3.activity2.guide.understanding.riskSource",
                        )}
                      </strong>{" "}
                      -{" "}
                      {t(
                        "workshop3.activity2.guide.understanding.riskSourceDesc",
                      )}
                    </li>
                    <li>
                      <strong>
                        {t(
                          "workshop3.activity2.guide.understanding.targetObjective",
                        )}
                      </strong>{" "}
                      -{" "}
                      {t(
                        "workshop3.activity2.guide.understanding.targetObjectiveDesc",
                      )}
                    </li>
                    <li>
                      <strong>
                        {t(
                          "workshop3.activity2.guide.understanding.dreadedEvent",
                        )}
                      </strong>{" "}
                      -{" "}
                      {t(
                        "workshop3.activity2.guide.understanding.dreadedEventDesc",
                      )}
                    </li>
                    <li>
                      <strong>
                        {t(
                          "workshop3.activity2.guide.understanding.businessValue",
                        )}
                      </strong>{" "}
                      -{" "}
                      {t(
                        "workshop3.activity2.guide.understanding.businessValueDesc",
                      )}
                    </li>
                    <li>
                      <strong>
                        {t(
                          "workshop3.activity2.guide.understanding.controlMeasures",
                        )}
                      </strong>{" "}
                      -{" "}
                      {t(
                        "workshop3.activity2.guide.understanding.controlMeasuresDesc",
                      )}
                    </li>
                    <li>
                      <strong>
                        {t(
                          "workshop3.activity2.guide.understanding.stakeholders",
                        )}
                      </strong>{" "}
                      -{" "}
                      {t(
                        "workshop3.activity2.guide.understanding.stakeholdersDesc",
                      )}
                    </li>
                  </ul>
                </div>

                <div className="bg-amber-50 p-4 rounded-lg border border-amber-100">
                  <h3 className="font-semibold text-amber-800 mb-2">
                    {t("workshop3.activity2.guide.actions.title")}
                  </h3>
                  <ul className="list-disc pl-5 space-y-1">
                    <li>{t("workshop3.activity2.guide.actions.search")}</li>
                    <li>{t("workshop3.activity2.guide.actions.filter")}</li>
                    <li>{t("workshop3.activity2.guide.actions.sort")}</li>
                    <li>
                      {t(
                        "workshop3.activity2.guide.actions.selectStakeholders",
                      )}
                    </li>
                  </ul>
                </div>
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setIsGuideOpen(false)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition duration-200"
                >
                  {t("common.close")}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  },
);

export default StrategicScenariosTable;
